/* eslint-disable @nx/enforce-module-boundaries */
import {
  DocumentReference,
  type AtLeast,
  type INamedDocument,
  type ISODateType,
  type ISoftDelete,
  type Timezone,
  Timestamp,
  TypeGuard,
  isTimestamp,
  isDocRef,
} from '@principle-theorem/shared';
import { type ICustomMappings } from './custom-mappings';
import { type IDestination, type IDestinationHandler } from './destination';
import { type ISource, type ISourceHandler } from './source';
import { type IDatabaseInstanceDocument } from './sql-database';
import {
  type ITranslationMap,
  type ITranslationMapHandler,
} from './translation-mappings';
import { ConnectorConfig } from 'firebase/data-connect';
import { IOrganisation } from 'libs/principle-core/interfaces/src/lib/organisation/organisation';
import { IBrand } from 'libs/principle-core/interfaces/src/lib/brand';
import { IPractice } from 'libs/principle-core/interfaces/src/lib/practice/practice';
import { IStaffer } from 'libs/principle-core/interfaces/src/lib/staffer/staffer';
import { isString } from 'lodash';

export enum PracticeMigrationStatus {
  Pending = 'pending',
  InProgress = 'inProgress',
  Complete = 'complete',
}

export const PRACTICE_MIGRATION_STATUS_MAP: {
  [key in PracticeMigrationStatus]: string;
} = {
  [PracticeMigrationStatus.Pending]: 'default',
  [PracticeMigrationStatus.InProgress]: 'accent',
  [PracticeMigrationStatus.Complete]: 'primary',
};

export const PRACTICE_MIGRATIONS_PROJECT = 'practice-migrations';

export const PRACTICE_MIGRATIONS_DATABASE = 'practice-migrations';

export const PRACTICE_MIGRATIONS_FIRESTORE_DATABASE = 'practice-migrations';

export const PRACTICE_MIGRATION_DATABASE_BACKUP_FOLDER = 'backup-files';

export interface IPracticeMigration extends ISoftDelete {
  metadata: IPracticeMigrationMetadata;
  configuration: IPracticeMigrationConfiguration;
  source: ISource;
  destination: IDestination;
  mappings: ICustomMappings;
  status: PracticeMigrationStatus;
  translationMaps: ITranslationMap[];
  manualTranslationMap: ITranslationMap[];
  instanceRef?: DocumentReference<IDatabaseInstanceDocument>;
}

export type PracticeMigrationEssentials = Pick<
  IPracticeMigration,
  'metadata' | 'source' | 'destination' | 'mappings'
>;

export interface IPracticeMigrationMetadata {
  name: string;
  version: string;
  supportsMultiplePractices: boolean;
  description?: string;
}

export interface IPracticeMigrationConfiguration {
  organisation: INamedDocument<IOrganisation>;
  brand: INamedDocument<IBrand>;
  practices: INamedDocument<IPractice>[];
  restrictAccessTo?: DocumentReference<IStaffer>[];
  projectId: string;
  destinationBucket: string;
  timezone: Timezone;
  backupDate: ISODateType;
  backupPath?: string;
  databaseInstanceName?: string;
  databaseName?: string;
  serviceAccount?: string;
  dataConnect?: ConnectorConfig;
}

export interface IPracticeMigrationHandler {
  source: ISourceHandler;
  destination: IDestinationHandler;
  translationMap: ITranslationMapHandler;
}

export enum PracticeMigrationCollection {
  TranslationMaps = 'translationMaps',
  SourceEntities = 'sourceEntities',
  DestinationEntities = 'destinationEntities',
  CustomMappings = 'customMappings',
  Notes = 'notes',
  Exports = 'exports',
}

export enum WorkspaceExportFileType {
  Migration = 'migration',
  OrganisationData = 'organisationData',
}

export interface IWorkspaceExportFile {
  type: WorkspaceExportFileType;
  path: string;
}

function isWorkspaceExportFile(data: unknown): data is IWorkspaceExportFile {
  return TypeGuard.interface<IWorkspaceExportFile>({
    type: TypeGuard.enumValue(WorkspaceExportFileType),
    path: isString,
  })(data);
}

export enum WorkspaceExportStatus {
  Pending = 'pending',
  Complete = 'complete',
  Failed = 'failed',
}

export interface IWorkspaceExportResult {
  status: WorkspaceExportStatus;
  completedAt?: ISODateType;
  errorMessage?: string;
}

function isWorkspaceExportResult(
  data: unknown
): data is IWorkspaceExportResult {
  return TypeGuard.interface<IWorkspaceExportResult>({
    status: TypeGuard.enumValue(WorkspaceExportStatus),
    completedAt: TypeGuard.nilOr(isString),
    errorMessage: TypeGuard.nilOr(isString),
  })(data);
}

export interface IWorkspaceExportManifest {
  exportId: string;
  exportedAt: ISODateType;
  sourceMigrationRef: DocumentReference<IPracticeMigration>;
  status: WorkspaceExportStatus;
  files: IWorkspaceExportFile[];
  result: IWorkspaceExportResult;
}

export function isWorkspaceExportManifest(
  data: unknown
): data is IWorkspaceExportManifest {
  return TypeGuard.interface<IWorkspaceExportManifest>({
    exportId: isString,
    exportedAt: isString,
    sourceMigrationRef: isDocRef,
    status: TypeGuard.enumValue(WorkspaceExportStatus),
    files: TypeGuard.arrayOf(isWorkspaceExportFile),
    result: isWorkspaceExportResult,
  })(data);
}

export interface IWorkspaceExport {
  exportId: string;
  status: WorkspaceExportStatus;
  completedAt?: Timestamp;
  errorMessage?: string;
  manifestPath?: string; // gs:// path to manifest
}

export function isWorkspaceExport(data: unknown): data is IWorkspaceExport {
  return TypeGuard.interface<IWorkspaceExport>({
    exportId: isString,
    status: TypeGuard.enumValue(WorkspaceExportStatus),
    completedAt: TypeGuard.nilOr(isTimestamp),
    errorMessage: TypeGuard.nilOr(isString),
    manifestPath: TypeGuard.nilOr(isString),
  })(data);
}

export type PracticeMigrationInitData = PracticeMigrationEssentials &
  AtLeast<IPracticeMigration, 'configuration'>;
